import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { format, isToday, parseISO, isBefore, addMinutes } from 'date-fns';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import { useMedicines } from '../../hooks/useMedicines';
import { useAuth } from '../../contexts/AuthContext';

const { FiClock, FiCheck, FiX, FiMoreVertical, FiAlertCircle } = FiIcons;

const UpcomingMedicines = () => {
  const { medicines, loading, logMedicineIntake } = useMedicines();
  const { user } = useAuth();
  const [todaySchedule, setTodaySchedule] = useState([]);
  const [takingMedicine, setTakingMedicine] = useState(null);

  useEffect(() => {
    if (medicines.length > 0) {
      generateTodaySchedule();
    }
  }, [medicines]);

  const generateTodaySchedule = () => {
    const today = new Date();
    const schedule = [];

    medicines.forEach(medicine => {
      if (medicine.timing && Array.isArray(medicine.timing)) {
        medicine.timing.forEach(time => {
          if (time) {
            const [hours, minutes] = time.split(':');
            const scheduledTime = new Date();
            scheduledTime.setHours(parseInt(hours), parseInt(minutes), 0, 0);

            // Check if this time slot is for today based on frequency
            const shouldShowToday = shouldShowMedicineToday(medicine, scheduledTime);
            
            if (shouldShowToday) {
              const now = new Date();
              const isOverdue = isBefore(scheduledTime, addMinutes(now, -30)); // 30 min grace period
              
              schedule.push({
                id: `${medicine.id}-${time}`,
                medicineId: medicine.id,
                name: medicine.name,
                dosage: medicine.dosage,
                type: medicine.type,
                time: time,
                scheduledTime: scheduledTime,
                foodInstruction: medicine.food_instruction,
                status: 'pending',
                overdue: isOverdue,
                medicine: medicine
              });
            }
          }
        });
      }
    });

    // Sort by time
    schedule.sort((a, b) => a.scheduledTime - b.scheduledTime);
    setTodaySchedule(schedule);
  };

  const shouldShowMedicineToday = (medicine, scheduledTime) => {
    const today = new Date();
    const startDate = medicine.start_date ? parseISO(medicine.start_date) : today;
    const endDate = medicine.end_date ? parseISO(medicine.end_date) : null;

    // Check if today is within the medicine's date range
    if (isBefore(today, startDate)) return false;
    if (endDate && isBefore(endDate, today)) return false;

    // Check frequency
    if (medicine.frequency === 'daily') return true;
    
    if (medicine.frequency === 'weekly' && medicine.selected_days) {
      const dayOfWeek = today.getDay(); // 0 = Sunday, 1 = Monday, etc.
      const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
      return medicine.selected_days.includes(dayNames[dayOfWeek]);
    }

    if (medicine.frequency === 'as_needed') return true;

    return true; // Default to showing
  };

  const getStatusColor = (status, overdue) => {
    if (overdue) return 'text-red-600 bg-red-50';
    if (status === 'taken') return 'text-green-600 bg-green-50';
    return 'text-blue-600 bg-blue-50';
  };

  const getStatusIcon = (status, overdue) => {
    if (overdue) return FiAlertCircle;
    if (status === 'taken') return FiCheck;
    return FiClock;
  };

  const getFoodInstructionText = (instruction) => {
    const instructions = {
      before_food: 'Before food',
      after_food: 'After food',
      with_food: 'With food',
      empty_stomach: 'Empty stomach',
      anytime: 'Anytime'
    };
    return instructions[instruction] || instruction;
  };

  const handleMarkAsTaken = async (scheduleItem) => {
    if (takingMedicine === scheduleItem.id) return;
    
    try {
      setTakingMedicine(scheduleItem.id);
      
      // Log the medicine intake
      await logMedicineIntake(
        scheduleItem.medicineId, 
        'taken', 
        `Taken at ${format(new Date(), 'HH:mm')} for ${scheduleItem.time} dose`
      );

      // Update the local state to reflect the change
      setTodaySchedule(prev => 
        prev.map(item => 
          item.id === scheduleItem.id 
            ? { ...item, status: 'taken', overdue: false }
            : item
        )
      );

    } catch (error) {
      console.error('Error marking medicine as taken:', error);
    } finally {
      setTakingMedicine(null);
    }
  };

  const handleSkipDose = async (scheduleItem) => {
    try {
      setTakingMedicine(scheduleItem.id);
      
      // Log as skipped
      await logMedicineIntake(
        scheduleItem.medicineId, 
        'skipped', 
        `Skipped ${scheduleItem.time} dose`
      );

      // Update the local state
      setTodaySchedule(prev => 
        prev.map(item => 
          item.id === scheduleItem.id 
            ? { ...item, status: 'skipped', overdue: false }
            : item
        )
      );

    } catch (error) {
      console.error('Error skipping medicine:', error);
    } finally {
      setTakingMedicine(null);
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
        <div className="flex items-center justify-center h-48">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-bold text-gray-900">Today's Schedule</h2>
        <div className="text-sm text-gray-500">
          {format(new Date(), 'EEEE, MMMM d')}
        </div>
      </div>

      <div className="space-y-4">
        {todaySchedule.map((scheduleItem, index) => (
          <motion.div
            key={scheduleItem.id}
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.3, delay: index * 0.1 }}
            className={`p-4 rounded-xl border-2 transition-all duration-200 hover:shadow-md ${
              scheduleItem.overdue 
                ? 'border-red-200 bg-red-50' 
                : scheduleItem.status === 'taken'
                ? 'border-green-200 bg-green-50'
                : scheduleItem.status === 'skipped'
                ? 'border-yellow-200 bg-yellow-50'
                : 'border-gray-200 bg-white hover:border-blue-200'
            }`}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${getStatusColor(scheduleItem.status, scheduleItem.overdue)}`}>
                  <SafeIcon 
                    icon={getStatusIcon(scheduleItem.status, scheduleItem.overdue)} 
                    className="text-lg" 
                  />
                </div>
                
                <div className="flex-1">
                  <div className="flex items-center space-x-2">
                    <h3 className="font-semibold text-gray-900">{scheduleItem.name}</h3>
                    <span className="text-sm text-gray-500">
                      {scheduleItem.dosage} • {scheduleItem.type}
                    </span>
                  </div>
                  <div className="flex items-center space-x-4 mt-1">
                    <span className="text-sm font-medium text-gray-700">
                      {scheduleItem.time}
                    </span>
                    <span className="text-sm text-gray-500">
                      {getFoodInstructionText(scheduleItem.foodInstruction)}
                    </span>
                    {scheduleItem.overdue && (
                      <span className="text-xs font-medium text-red-600 bg-red-100 px-2 py-1 rounded-full">
                        Overdue
                      </span>
                    )}
                  </div>
                </div>
              </div>

              <div className="flex items-center space-x-2">
                {scheduleItem.status === 'pending' && (
                  <>
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={() => handleMarkAsTaken(scheduleItem)}
                      disabled={takingMedicine === scheduleItem.id}
                      className={`px-4 py-2 text-white text-sm font-medium rounded-lg transition-colors ${
                        scheduleItem.overdue 
                          ? 'bg-red-600 hover:bg-red-700' 
                          : 'bg-blue-600 hover:bg-blue-700'
                      } ${takingMedicine === scheduleItem.id ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      {takingMedicine === scheduleItem.id ? (
                        <div className="flex items-center space-x-2">
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                          <span>Processing...</span>
                        </div>
                      ) : scheduleItem.overdue ? (
                        'Take Now'
                      ) : (
                        'Mark as Taken'
                      )}
                    </motion.button>
                    
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={() => handleSkipDose(scheduleItem)}
                      disabled={takingMedicine === scheduleItem.id}
                      className="px-3 py-2 text-gray-600 text-sm font-medium border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                    >
                      Skip
                    </motion.button>
                  </>
                )}

                {scheduleItem.status === 'taken' && (
                  <span className="px-4 py-2 bg-green-100 text-green-700 text-sm font-medium rounded-lg">
                    ✓ Taken
                  </span>
                )}

                {scheduleItem.status === 'skipped' && (
                  <span className="px-4 py-2 bg-yellow-100 text-yellow-700 text-sm font-medium rounded-lg">
                    Skipped
                  </span>
                )}

                <button className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors">
                  <SafeIcon icon={FiMoreVertical} />
                </button>
              </div>
            </div>

            {/* Additional info for overdue medicines */}
            {scheduleItem.overdue && (
              <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm text-red-700">
                  <SafeIcon icon={FiAlertCircle} className="inline mr-2" />
                  This dose was scheduled for {scheduleItem.time}. Take it now or mark as skipped.
                </p>
              </div>
            )}
          </motion.div>
        ))}
      </div>

      {todaySchedule.length === 0 && (
        <div className="text-center py-12">
          <SafeIcon icon={FiCheck} className="text-6xl text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">All caught up!</h3>
          <p className="text-gray-500">
            {medicines.length === 0 
              ? "No medicines added yet. Add your first medicine to get started."
              : "No medicines scheduled for today."
            }
          </p>
        </div>
      )}

      {/* Summary */}
      {todaySchedule.length > 0 && (
        <div className="mt-6 p-4 bg-gray-50 rounded-xl">
          <div className="flex items-center justify-between text-sm">
            <span className="text-gray-600">
              Today's Progress: {todaySchedule.filter(item => item.status === 'taken').length} of {todaySchedule.length} doses taken
            </span>
            <div className="flex items-center space-x-4">
              {todaySchedule.filter(item => item.overdue).length > 0 && (
                <span className="text-red-600 font-medium">
                  {todaySchedule.filter(item => item.overdue).length} overdue
                </span>
              )}
              <div className="w-20 bg-gray-200 rounded-full h-2">
                <div 
                  className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                  style={{ 
                    width: `${(todaySchedule.filter(item => item.status === 'taken').length / todaySchedule.length) * 100}%` 
                  }}
                ></div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UpcomingMedicines;